import React, { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { Phone, Mail, MapPin, Send } from "lucide-react";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", service: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return setStatus({ loading: false, ok: false, error: "Заповніть імʼя та телефон" });
    setStatus({ loading: true, ok: false, error: "" });
    try {
      await api.post("/leads", { ...form, source: "contact" });
      setStatus({ loading: false, ok: true, error: "" });
      setForm({ name: "", phone: "", email: "", city: "", service: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-charcoal py-24 lg:py-40">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-10">
        {/* Left */}
        <div className="col-span-12 lg:col-span-5">
          <div className="eyebrow mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-brass" /> {t.contact.eyebrow}
          </div>
          <h2 className="font-display text-[2.4rem] sm:text-5xl lg:text-6xl text-bone leading-[1] tracking-tight">
            {t.contact.title_1} <span className="italic-display text-brass">{t.contact.title_2}</span>
          </h2>
          <p className="mt-6 text-bone/70 leading-relaxed max-w-md">{t.contact.lead}</p>

          <div className="mt-10 space-y-6">
            <a href="tel:+380957955234" data-testid="contact-phone-1" className="flex items-start gap-4 group">
              <span className="mt-1 w-9 h-9 border border-bone/15 grid place-items-center text-brass"><Phone className="w-4 h-4" /></span>
              <div>
                <div className="text-[0.7rem] tracking-[0.2em] uppercase text-taupe">Телефон</div>
                <div className="text-bone group-hover:text-brass transition-colors text-lg">+38 (095) 795-52-34</div>
                <div className="text-bone/60 text-sm">+38 (067) 337-00-91</div>
              </div>
            </a>
            <a href="mailto:info@dzhavala.ua" data-testid="contact-email" className="flex items-start gap-4 group">
              <span className="mt-1 w-9 h-9 border border-bone/15 grid place-items-center text-brass"><Mail className="w-4 h-4" /></span>
              <div>
                <div className="text-[0.7rem] tracking-[0.2em] uppercase text-taupe">Email</div>
                <div className="text-bone group-hover:text-brass transition-colors text-lg">info@dzhavala.ua</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="mt-1 w-9 h-9 border border-bone/15 grid place-items-center text-brass"><MapPin className="w-4 h-4" /></span>
              <div>
                <div className="text-[0.7rem] tracking-[0.2em] uppercase text-taupe">Адреса</div>
                <div className="text-bone text-lg">Київ, Україна</div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="col-span-12 lg:col-span-6 lg:col-start-7 bg-onyx p-8 lg:p-12 border border-bone/10"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <div>
              <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.contact.name}*</label>
              <input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="field"
                required
              />
            </div>
            <div>
              <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.contact.phone}*</label>
              <input
                data-testid="contact-phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="field"
                required
              />
            </div>
            <div>
              <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.contact.email}</label>
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="field"
              />
            </div>
            <div>
              <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.contact.city}</label>
              <input
                data-testid="contact-city"
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                className="field"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.contact.service}</label>
              <select
                data-testid="contact-service"
                value={form.service}
                onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                className="field bg-onyx"
              >
                <option value="">—</option>
                {t.services.list.map((s) => (
                  <option key={s.t} value={s.t}>{s.t}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.contact.message}</label>
              <textarea
                data-testid="contact-message"
                rows={3}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="field resize-none"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-xs text-taupe">{status.error && <span data-testid="contact-error" className="text-red-400">{status.error}</span>}{status.ok && <span data-testid="contact-success" className="text-brass">{t.contact.sent}</span>}</div>
            <button data-testid="contact-submit" type="submit" disabled={status.loading} className="btn-brass">
              <Send className="w-3.5 h-3.5" /> {status.loading ? "..." : t.cta.send}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
